import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";

import { PartnerClaim } from "../../api/partner_claims";
import { useDialogState } from "../../hooks/useDialogState";
import { useSnackbar } from "../../hooks/useSnackbar";
import { usePartnerClaimsStore } from "../../store/partnerClaimsStore";
import { ConfirmActionDialog } from "../common/ConfirmActionDialog";
import DelayedInput from "../common/DelayedInput";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { AttachClaimDialog } from "./AttachClaimDialog";
import {
  RESEND_MAX_SENDS,
  formatPaidAge,
  formatReminderHistory,
  formatSendTimes,
  matchedOnLabel,
  sortClaims,
  statusMeaning,
  type ClaimSort,
} from "./utils";

/**
 * Every partner purchase that has been paid for and never claimed.
 *
 * The worklist behind two runbooks: support opens it when someone says they paid
 * and cannot get in, and on-call sweeps it after a pipeline stall. Deliberately a
 * list with one action rather than a dashboard — at launch scale this is one
 * partner and tens of members, and the useful thing is a row you can act on.
 *
 * `InfinitePaginationTable` needs an `id` per item; the claim id is that id, and
 * it is also the one identifier that reconstructs a purchase end to end, so it is
 * shown rather than merely used.
 */
export const UnclaimedClaimsList = () => {
  const {
    claims,
    loading,
    hasLoaded,
    error,
    loadClaims,
    resendClaimEmail,
    searchHits,
    searching,
    searchClaims,
    clearSearch,
    removeClaim,
  } = usePartnerClaimsStore();
  const resendConfirm = useDialogState<PartnerClaim>();
  const attachDialog = useDialogState<PartnerClaim>();
  const { showSnackbar, SnackbarNode } = useSnackbar();
  const [resendingId, setResendingId] = React.useState<string | undefined>();
  const [sort, setSort] = React.useState<ClaimSort>("oldest");
  const searchedFor = React.useRef("");

  /**
   * Stable, and a no-op when the term has not actually changed.
   *
   * `DelayedInput` fires this from an effect keyed on the handler itself, and it
   * fires once on mount with an empty box. An inline arrow would be a new
   * identity every render, and since every store write re-renders this
   * component, the effect would re-run, write again, and never settle. Both
   * existing callers guard the same way.
   */
  const handleSearchTerm = React.useCallback(
    (text: string) => {
      const term = text.trim();
      if (term === searchedFor.current) return;
      searchedFor.current = term;
      if (term) searchClaims(term);
      else clearSearch();
    },
    [searchClaims, clearSearch]
  );

  React.useEffect(() => {
    loadClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async (claim: PartnerClaim) => {
    setResendingId(claim.claimId);
    try {
      const result = await resendClaimEmail(claim.claimId);
      if (!result.sent) {
        // A 200 that reports nothing was mailed. Saying "sent" here would have
        // support tell a member their link is on the way when it is not, and the
        // member wait for mail that never existed.
        showSnackbar(
          "The server accepted the request but reported no email was sent. Check the claim before trying again.",
          "warning"
        );
        return;
      }
      showSnackbar(
        `Claim link sent to ${claim.buyerEmail} — send ${result.reminderCount} of ${RESEND_MAX_SENDS}.`
      );
    } catch (err) {
      // Shown verbatim. The refusals are the answer to "why didn't that send" —
      // the cooldown and the ten-send cap are worded the way the member sees them
      // on the claim page, and a paraphrase here would have support and member
      // describing the same refusal differently.
      showSnackbar(
        err instanceof Error ? err.message : "Failed to resend the claim email.",
        "error"
      );
    } finally {
      setResendingId(undefined);
      resendConfirm.close();
    }
  };

  const columns = [
    {
      id: "buyerEmail",
      label: "Buyer",
      render: (claim: PartnerClaim) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {claim.buyerEmail ? (
            <Typography variant="body2">{claim.buyerEmail}</Typography>
          ) : (
            // Not a cosmetic gap: Stripe collected nothing, so there is no route
            // to this buyer at all and no resend will ever reach them.
            <Tooltip title="Stripe collected no address — only a support attach recovers this buyer">
              <Chip label="no address" size="small" color="warning" />
            </Tooltip>
          )}
          {/* Outside the branch above, deliberately: a `created` row often has
              no address either, and that is the row where "no payment recorded"
              matters most. Nesting this under the email would hide it there. */}
          {claim.status !== "paid" && (
            <Tooltip title={statusMeaning(claim.status)}>
              <Chip
                label={claim.status.replace(/_/g, " ")}
                size="small"
                color={claim.status === "created" ? "error" : "default"}
                // Focusable so the explanation is reachable from the keyboard,
                // not only on hover.
                tabIndex={0}
                aria-label={statusMeaning(claim.status)}
                sx={{ cursor: "help" }}
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
    ...(searchHits
      ? [
          {
            id: "evidence",
            label: "Matched on",
            render: (claim: PartnerClaim) => {
              const hit = evidenceFor.get(claim.claimId);
              if (!hit) return null;
              const card = [hit.evidence?.cardBrand, hit.evidence?.cardLast4]
                .filter(Boolean)
                .join(" ····");
              return (
                <Box>
                  <Chip label={matchedOnLabel(hit.matchedOn)} size="small" />
                  {(card || hit.evidence?.name) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {[hit.evidence?.name, card].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </Box>
              );
            },
          },
        ]
      : []),
    {
      id: "partnerName",
      label: "Partner",
      render: (claim: PartnerClaim) => (
        <Typography variant="body2">{claim.partnerName}</Typography>
      ),
    },
    {
      id: "offerSlug",
      label: "Offer",
      hideOnMobile: true,
      render: (claim: PartnerClaim) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {claim.offerSlug}
        </Typography>
      ),
    },
    {
      id: "paidAt",
      label: "Unclaimed for",
      render: (claim: PartnerClaim) => {
        // 14 days is the alarm threshold; anything past it has been waiting long
        // enough that it should already have paged someone.
        const overdue =
          claim.paidAt !== undefined &&
          Math.floor(Date.now() / 1000) - claim.paidAt > 14 * 24 * 60 * 60;
        return (
          <Typography
            variant="body2"
            sx={{ color: overdue ? "error.main" : "inherit", fontWeight: overdue ? 600 : 400 }}
          >
            {formatPaidAge(claim.paidAt)}
          </Typography>
        );
      },
    },
    {
      id: "reminders",
      label: "Reminders",
      render: (claim: PartnerClaim) => (
        <Tooltip title={formatSendTimes(claim.reminderSentAt)}>
          <Typography
            variant="body2"
            sx={{
              cursor: "help",
              color:
                claim.reminderCount >= RESEND_MAX_SENDS
                  ? "error.main"
                  : "inherit",
            }}
          >
            {formatReminderHistory(claim.reminderCount, claim.reminderSentAt)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: "claimId",
      label: "Claim ID",
      hideOnMobile: true,
      render: (claim: PartnerClaim) => (
        <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
          {claim.claimId}
        </Typography>
      ),
    },
    {
      // Never hidden behind a row expand, unlike the reference columns above:
      // resending is the single action this screen exists for, and a primary
      // action that needs two taps to find on a phone is a primary action
      // nobody uses.
      id: "actions",
      label: "Actions",
      render: (claim: PartnerClaim) => {
        const isResending = resendingId === claim.claimId;
        const capped = claim.reminderCount >= RESEND_MAX_SENDS;
        // A search can return rows the worklist never holds, and mailing a claim
        // link for one is its own harm: on a resolved row it sends a live link
        // for a purchase already attached to somebody, and on a `created` row it
        // invites a buyer to claim a payment we have no record of. The server
        // refuses both; this stops the button offering them.
        const resolvable = claim.status === "paid";
        const disabled =
          isResending || !resolvable || !claim.buyerEmail || capped;
        const title = !resolvable
          ? statusMeaning(claim.status)
          : !claim.buyerEmail
            ? "No address on file — use support attach"
            : capped
              ? "Lifetime send cap reached — use support attach"
              : "Resend claim email";
        return (
          <Tooltip title={title}>
            {/* A disabled IconButton fires no events, so the Tooltip needs a
                wrapper to hang off — and the disabled cases are exactly the ones
                whose explanation is worth reading. */}
            <span>
              <IconButton
                size="small"
                // Named explicitly rather than leaning on the Tooltip: the
                // wrapper span below takes the tooltip's aria, so without this
                // the button itself has no accessible name at all.
                aria-label={title}
                disabled={disabled}
                onClick={() => resendConfirm.openWith(claim)}
                sx={{ color: "#1976d2" }}
              >
                {isResending ? (
                  <CircularProgress size={16} />
                ) : (
                  <EmailIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: "attach",
      label: "",
      render: (claim: PartnerClaim) => {
        // Refused server-side too; disabled here so the reason is readable
        // before the click rather than arriving as a rejection after it.
        const attachable = claim.status === "paid";
        const title = attachable
          ? "Attach to an account — for a buyer whose address cannot receive mail"
          : statusMeaning(claim.status);
        return (
          <Tooltip title={title}>
            <span>
              <IconButton
                size="small"
                // Named with the reason, matching the resend column above: the
                // wrapping span takes the tooltip's aria, so a bare label would
                // leave a screen reader with no explanation at all.
                aria-label={attachable ? "Attach to an account" : title}
                disabled={!attachable}
                onClick={() => attachDialog.openWith(claim)}
                sx={{ color: "#1976d2" }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  // A search answers from Stripe as well as the worklist, so it can surface a
  // purchase the table never held. When one is running it replaces the list
  // rather than filtering it.
  // Evidence is carried on the row, not discarded with the hit. The attach
  // dialog tells support to identify a purchase from the payment evidence, and
  // the name on the card and its last four digits are the only things that do
  // that — none of it is on the claim record.
  const evidenceFor = new Map(
    (searchHits ?? []).map((hit) => [hit.claim.claimId, hit])
  );
  const rows = sortClaims(
    searchHits ? searchHits.map((hit) => hit.claim) : claims,
    // Server match order is not meaningful across the modes, but an exact
    // last-4 hit sitting below weaker candidates would be; age is the stable
    // tiebreak either way.
    sort
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6">Unclaimed partner subscriptions</Typography>
          <Typography variant="body2" color="text.secondary">
            Paid for and never claimed, oldest first. Check the reminder history
            before resending — several sends with nothing landing usually means
            the address is wrong, which a support attach fixes and another email
            does not.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <DelayedInput
            size="small"
            label="Find a purchase"
            placeholder="Email, name on card, last 4, or any Stripe id"
            sx={{ minWidth: 280 }}
            onDelayedChange={handleSearchTerm}
          />
          <TextField
            select
            size="small"
            label="Sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as ClaimSort)}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="oldest">Oldest first</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </TextField>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              searchedFor.current = "";
              clearSearch();
              loadClaims();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* An empty table would read as "nobody is stuck", which is the one wrong
          answer this screen must not give silently. */}
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          Could not load the worklist: {error}
        </Typography>
      )}

      {/* Gated on a completed load, not on `!loading`: that is also true before
          the first request goes out, and this line would then assert nobody is
          stuck without having asked. */}
      {searchHits?.length === 0 && !searching && (
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ py: 2, textAlign: "center" }}
        >
          Nothing matched. Try the name on the card, its last four digits, or a
          Stripe id from the dashboard. Note Stripe takes about a minute to make
          a brand-new payment searchable.
        </Typography>
      )}

      {!searchHits && hasLoaded && !loading && !error && claims.length === 0 && (
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ py: 2, textAlign: "center" }}
        >
          Nothing unclaimed — every paid purchase has reached an account.
        </Typography>
      )}

      {(rows.length > 0 || loading || searching || (!hasLoaded && !searchHits)) && (
        <InfinitePaginationTable
          columns={columns}
          items={rows.map((claim) => ({ ...claim, id: claim.claimId }))}
          pageSize={25}
          loading={loading || searching}
          onLoadNext={() => {}}
        />
      )}

      <ConfirmActionDialog
        open={resendConfirm.open}
        title="Resend claim email?"
        message={
          <>
            A fresh 7-day link goes to{" "}
            <strong>{resendConfirm.data?.buyerEmail}</strong>, the address on the
            payment — earlier links stop working. This counts against the{" "}
            {RESEND_MAX_SENDS}-send limit for this purchase (
            {resendConfirm.data?.reminderCount ?? 0} used).
          </>
        }
        confirmLabel="Resend claim email"
        loading={resendingId !== undefined}
        onClose={resendConfirm.close}
        onConfirm={() => {
          if (resendConfirm.data) handleResend(resendConfirm.data);
        }}
      />

      <AttachClaimDialog
        open={attachDialog.open}
        claim={attachDialog.data}
        onClose={attachDialog.close}
        onAttached={(message) => {
          if (attachDialog.data) removeClaim(attachDialog.data.claimId);
          showSnackbar(message);
        }}
      />

      {SnackbarNode}
    </Box>
  );
};
